import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { 
  BarChart3, 
  Users, 
  FileQuestion, 
  Trophy, 
  MessageSquare, 
  BookOpen,
  TrendingUp,
  UserPlus,
  CreditCard,
  Clock,
  CheckCircle,
  XCircle,
  Calendar
} from "lucide-react";
import { format, subDays } from "date-fns";

interface StatsData {
  period: string;
  startDate: string;
  endDate: string;
  stats: {
    testsTaken: number;
    questionsAnswered: number;
    correctAnswers: number;
    correctRate: number;
    totalSignups: number;
    partialSignups: number;
    paidSignups: number;
    trialSignups: number;
    activeSubscribers: number;
    totalUsers: number;
    totalQuestions: number;
    readingCompleted: number;
    prizesClaimed: number;
    messagesSent: number;
  };
  dailyActivity: Array<{
    date: string;
    tests: number;
    questions: number;
  }>;
}

function StatCard({ 
  title, 
  value, 
  icon: Icon, 
  description,
  trend,
  variant = "default"
}: { 
  title: string; 
  value: number | string; 
  icon: any;
  description?: string;
  trend?: string;
  variant?: "default" | "success" | "warning" | "info";
}) {
  const variantStyles = {
    default: "bg-card",
    success: "bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800",
    warning: "bg-yellow-50 dark:bg-yellow-950/30 border-yellow-200 dark:border-yellow-800",
    info: "bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800"
  };

  return (
    <Card className={variantStyles[variant]}>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{typeof value === 'number' ? value.toLocaleString() : value}</div>
        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
        {trend && (
          <div className="flex items-center gap-1 mt-1">
            <TrendingUp className="h-3 w-3 text-green-500" />
            <span className="text-xs text-green-500">{trend}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ActivityChart({ data }: { data: StatsData['dailyActivity'] }) {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-muted-foreground">
        No activity data available for this period
      </div>
    );
  }

  const maxQuestions = Math.max(...data.map(d => d.questions), 1);
  const maxTests = Math.max(...data.map(d => d.tests), 1);
  const maxValue = Math.max(maxQuestions, maxTests);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-primary rounded" />
          <span>Questions Answered</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-green-500 rounded" />
          <span>Tests Taken</span>
        </div>
      </div>
      <div className="flex items-end gap-1 h-48">
        {data.map((day, index) => {
          const questionHeight = (day.questions / maxValue) * 100;
          const testHeight = (day.tests / maxValue) * 100;
          const dateStr = new Date(day.date).toLocaleDateString('en-US', { timeZone: "America/Jamaica", weekday: 'short', month: 'short', day: 'numeric' });
          
          return (
            <div key={index} className="flex-1 flex flex-col items-center gap-1 group relative">
              <div className="flex items-end gap-0.5 h-40 w-full">
                <div 
                  className="flex-1 bg-primary rounded-t transition-all hover:opacity-80"
                  style={{ height: `${Math.max(questionHeight, 2)}%` }}
                  title={`${day.questions} questions`}
                />
                <div 
                  className="flex-1 bg-green-500 rounded-t transition-all hover:opacity-80"
                  style={{ height: `${Math.max(testHeight, 2)}%` }}
                  title={`${day.tests} tests`}
                />
              </div>
              <span className="text-xs text-muted-foreground truncate w-full text-center" title={dateStr}>
                {data.length <= 14 ? dateStr.split(' ')[1] : (index % 3 === 0 ? dateStr.split(' ')[1] : '')}
              </span>
              <div className="absolute -top-16 left-1/2 transform -translate-x-1/2 bg-popover border rounded-md p-2 shadow-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 whitespace-nowrap text-xs">
                <div className="font-medium">{dateStr}</div>
                <div>Questions: {day.questions}</div>
                <div>Tests: {day.tests}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminStats() {
  const [period, setPeriod] = useState("7d");
  const [customStart, setCustomStart] = useState(() => format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [customEnd, setCustomEnd] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [customOpen, setCustomOpen] = useState(false);

  const queryUrl = period === 'custom' 
    ? `/api/admin/stats?period=custom&start=${customStart}&end=${customEnd}`
    : `/api/admin/stats?period=${period}`;

  const { data, isLoading, error } = useQuery<StatsData>({
    queryKey: [queryUrl]
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">Platform Statistics</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-2">
                <div className="h-4 bg-muted rounded w-24" />
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-muted rounded w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">Failed to load statistics</p>
      </div>
    );
  }

  const stats = data?.stats;
  const periodLabel = period === 'custom' 
    ? `${format(new Date(customStart), 'MMM d, yyyy')} - ${format(new Date(customEnd), 'MMM d, yyyy')}`
    : {
        '24h': 'Last 24 Hours',
        '7d': 'Last 7 Days',
        '30d': 'Last 30 Days',
        '90d': 'Last 90 Days',
        'all': 'All Time'
      }[period] || 'Last 7 Days';

  const handleApplyCustom = () => {
    setPeriod('custom');
    setCustomOpen(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold">Platform Statistics</h2>
          <p className="text-muted-foreground">Overview of platform activity and engagement</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={period === 'custom' ? 'custom' : period} onValueChange={(val) => {
            if (val !== 'custom') {
              setPeriod(val);
            }
          }}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24h">Last 24 Hours</SelectItem>
              <SelectItem value="7d">Last 7 Days</SelectItem>
              <SelectItem value="30d">Last 30 Days</SelectItem>
              <SelectItem value="90d">Last 90 Days</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
              {period === 'custom' && <SelectItem value="custom">Custom Range</SelectItem>}
            </SelectContent>
          </Select>
          <Popover open={customOpen} onOpenChange={setCustomOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="icon" title="Custom date range">
                <Calendar className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="end">
              <div className="space-y-4">
                <h4 className="font-medium">Custom Date Range</h4>
                <div className="space-y-2">
                  <Label htmlFor="start-date">Start Date</Label>
                  <Input
                    id="start-date"
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    max={customEnd}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end-date">End Date</Label>
                  <Input
                    id="end-date"
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    min={customStart}
                    max={format(new Date(), 'yyyy-MM-dd')}
                  />
                </div>
                <Button onClick={handleApplyCustom} className="w-full">
                  Apply
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Learning Activity ({periodLabel})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Tests Taken"
              value={stats?.testsTaken || 0}
              icon={FileQuestion}
              description="Total quiz submissions"
            />
            <StatCard
              title="Questions Answered"
              value={stats?.questionsAnswered || 0}
              icon={CheckCircle}
              description="Individual question attempts"
            />
            <StatCard
              title="Correct Answers"
              value={stats?.correctAnswers || 0}
              icon={TrendingUp}
              description={`${stats?.correctRate || 0}% accuracy rate`}
              variant="success"
            />
            <StatCard
              title="Reading Completed"
              value={stats?.readingCompleted || 0}
              icon={BookOpen}
              description="Reading materials finished"
            />
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Users className="h-5 w-5" />
            User Growth ({periodLabel})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Total Signups"
              value={stats?.totalSignups || 0}
              icon={UserPlus}
              description="New registrations"
            />
            <StatCard
              title="Free Users"
              value={stats?.partialSignups || 0}
              icon={Clock}
              description="Registered but never subscribed"
              variant="warning"
            />
            <StatCard
              title="Converted to Paid"
              value={stats?.paidSignups || 0}
              icon={CreditCard}
              description="Subscribed during period"
              variant="success"
            />
            <StatCard
              title="Trial Users"
              value={stats?.trialSignups || 0}
              icon={Clock}
              description="Active trial users"
              variant="info"
            />
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Platform Overview (All Time)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Active Subscribers"
              value={stats?.activeSubscribers || 0}
              icon={CreditCard}
              description="Currently paying users"
              variant="success"
            />
            <StatCard
              title="Total Users"
              value={stats?.totalUsers || 0}
              icon={Users}
              description="All registered users"
            />
            <StatCard
              title="Question Bank"
              value={stats?.totalQuestions || 0}
              icon={FileQuestion}
              description="Questions available"
            />
            <StatCard
              title="Prizes Claimed"
              value={stats?.prizesClaimed || 0}
              icon={Trophy}
              description="Rewards redeemed"
            />
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Daily Activity
            </CardTitle>
            <CardDescription>Questions answered and tests taken per day</CardDescription>
          </CardHeader>
          <CardContent>
            <ActivityChart data={data?.dailyActivity || []} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Engagement Metrics ({periodLabel})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <div className="text-3xl font-bold text-primary">{stats?.messagesSent?.toLocaleString() || 0}</div>
                <div className="text-sm text-muted-foreground mt-1">Messages Sent</div>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <div className="text-3xl font-bold text-primary">
                  {stats?.testsTaken && stats?.totalSignups 
                    ? (stats.testsTaken / Math.max(stats.totalSignups, 1)).toFixed(1)
                    : '0'}
                </div>
                <div className="text-sm text-muted-foreground mt-1">Tests per User (avg)</div>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <div className="text-3xl font-bold text-primary">
                  {stats?.paidSignups && stats?.totalSignups 
                    ? Math.round((stats.paidSignups / Math.max(stats.totalSignups, 1)) * 100)
                    : 0}%
                </div>
                <div className="text-sm text-muted-foreground mt-1">Conversion Rate</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
